import { Times, TwoArrowExpand, TwoArrowCollapse } from '@assets/svgs';
import { Button } from '@components/button';
import * as styles from '@components/dialog/Dialog.styles';
import { IconButton } from '@components/iconButton';
import { Tooltip } from '@components/tooltip';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  SxProps,
  Typography,
  Theme,
  ThemeProvider,
  Box,
} from '@mui/material';

import { useComposeTheme } from '@theme/theme';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { FunctionComponent, MouseEvent, ReactNode, SyntheticEvent, isValidElement, useState } from 'react';

export type DialogType = 'default' | 'defaultStepper' | 'scrollable' | 'scrollableWithTitle' | 'confirmation';

export type DialogSize = 'small' | 'medium' | 'large' | 'extraLarge' | 'fullWidth';

export interface DialogProps extends MuiDialogProps {
  children?: ReactNode;
  footerContent?: ReactNode;
  handleCloseDialog?: (e?: SyntheticEvent) => void;
  isCloseButtonDisabled?: boolean;
  closeIcon?: React.ReactNode;
  isPrimaryBtnDisabled?: boolean;
  isPrimaryBtnLoading?: boolean;
  /** Hover text shown on the primary button; most useful for explaining why it's disabled. */
  primaryBtnTooltip?: string;
  isSecondaryBtnDisabled?: boolean;
  isTertiaryBtnDisabled?: boolean;
  tertiaryBtnLoading?: boolean;
  primaryBtnAction?: (event: MouseEvent<HTMLElement>) => void;
  primaryBtnText?: string;
  secondaryBtnAction?: () => void;
  secondaryBtnText?: string;
  showBorders?: boolean;
  size?: DialogSize;
  tertiaryBtnAction?: () => void;
  tertiaryBtnText?: string;
  type?: DialogType;
  allowFullscreen?: boolean;
  sxProps?: {
    dialogActionsSx?: SxProps;
    title?: SxProps;
    paperSx?: SxProps;
    titleContainerSx?: SxProps;
    dialogContent?: SxProps;
  };
}

export type DescriptionType = {
  id: string | number;
  value: string;
};

export interface IDialogContentProps {
  title?: string;
  subTitle?: string;
  description?: string | DescriptionType[] | ReactNode;
}

export const Dialog: FunctionComponent<DialogProps & IDialogContentProps> = ({
  children,
  description,
  footerContent,
  handleCloseDialog,
  closeIcon,
  isCloseButtonDisabled = false,
  isPrimaryBtnDisabled = false,
  isPrimaryBtnLoading = false,
  primaryBtnTooltip,
  isSecondaryBtnDisabled = false,
  isTertiaryBtnDisabled = false,
  tertiaryBtnLoading = false,
  open,
  primaryBtnAction,
  primaryBtnText,
  secondaryBtnAction,
  secondaryBtnText,
  showBorders = true,
  size = 'small',
  tertiaryBtnAction,
  tertiaryBtnText,
  title,
  subTitle,
  type = 'default',
  allowFullscreen = false,
  fullScreen,
  sxProps,
  ...other
}) => {
  const isConfirmation = type === 'confirmation';
  const [isFullscreen, setIsFullscreen] = useState(fullScreen);
  let dialogContent: undefined | ReactNode;

  const theme = useComposeTheme('light');
  const { isMobileOrSmallScreen } = useGetScreenSize();

  if (Array.isArray(description)) {
    dialogContent = description.map((item: DescriptionType) => (
      <DialogContentText key={item.id} variant='body1'>
        {item.value}
      </DialogContentText>
    ));
  } else if (typeof description === 'string') {
    dialogContent = (
      <DialogContentText variant='body1' sx={{ padding: '20px' }}>
        {description}
      </DialogContentText>
    );
  } else if (isValidElement(description)) {
    dialogContent = description;
  }

  return (
    <ThemeProvider theme={theme}>
      <MuiDialog
        open={open}
        fullScreen={isMobileOrSmallScreen || isFullscreen}
        slotProps={{
          paper: {
            sx: (theme: Theme) => ({
              // Enable flex column so DialogContent can expand and allow nested scroll areas
              display: 'flex',
              flexDirection: 'column',
              padding: isFullscreen ? '8px 16px' : '24px 40px',
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.boxShadow[4],
              backgroundColor: 'drawerNavigation.main',
              ...(!isConfirmation ? styles.dialogTypeSize[size] : {}),
              ...(type ? styles.dialogTypeStyle[type] : {}),
              ...(isFullscreen && { width: '100%', height: '100%', maxWidth: 'unset', maxHeight: 'unset' }),
              ...(sxProps?.paperSx ? (sxProps.paperSx as any) : {}),
            }),
          },
        }}
        {...other}
      >
        {title ? (
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: { xs: 'baseline', sm: 'center' },
              justifyContent: 'space-between',
              height: { xs: 'fit-content' },
              minHeight: '60px',
              padding: '0 10px',
              flexDirection: 'row',
              borderBottom: (theme) => (showBorders ? theme.borders.main : null),
              ...(sxProps?.titleContainerSx ? (sxProps.titleContainerSx as any) : {}),
            }}
          >
            <Grid
              size={allowFullscreen ? undefined : { xs: 11 }}
              sx={{
                ...(allowFullscreen ? { flex: 1, minWidth: 0 } : {}),
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  py: { xs: 2, sm: 1 },
                  maxWidth: { xs: '80vw' },
                  marginLeft: { xs: '8px', sm: 2 },
                  ...sxProps?.title,
                }}
              >
                {title}
              </Typography>
              {subTitle && (
                <Typography
                  display='block'
                  variant='caption'
                  sx={{ color: 'text.secondary', marginLeft: { xs: '8px', sm: '15px' } }}
                >
                  {subTitle}
                </Typography>
              )}
            </Grid>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...(allowFullscreen ? { flexShrink: 0 } : {}) }}
            >
              {allowFullscreen && !isMobileOrSmallScreen && (
                <Tooltip enterDelay={1000} title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                  <IconButton
                    size='small'
                    sx={{ svg: { width: 14, height: 14, color: 'text.main' } }}
                    onClick={() => setIsFullscreen((f) => !f)}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    {isFullscreen ? <TwoArrowCollapse /> : <TwoArrowExpand />}
                  </IconButton>
                </Tooltip>
              )}
              {handleCloseDialog && (
                <IconButton
                  sx={{ svg: { width: closeIcon ? 'auto' : '10px', color: 'text.main' } }}
                  disabled={isCloseButtonDisabled}
                  onClick={(e) => handleCloseDialog(e)}
                >
                  {closeIcon ?? <Times />}
                </IconButton>
              )}
            </Box>
          </Grid>
        ) : null}

        <DialogContent
          sx={{
            maxHeight: isFullscreen ? 'unset' : '80vh',
            overflowY: 'auto',
            ...sxProps?.dialogContent,
          }}
        >
          {description ? dialogContent : children}
        </DialogContent>
        {footerContent}
        {primaryBtnText || secondaryBtnText ? (
          <DialogActions
            sx={{
              borderTop: showBorders ? (theme: Theme) => theme.borders.main : '',
            }}
          >
            {tertiaryBtnText ? (
              <Grid
                sx={{
                  marginLeft: 2,
                  flexGrow: 3,
                }}
              >
                <Button
                  variant='text'
                  onClick={tertiaryBtnAction}
                  disabled={isTertiaryBtnDisabled}
                  sx={{ width: 'fit-content' }}
                  isLoading={tertiaryBtnLoading}
                >
                  {tertiaryBtnText}
                </Button>
              </Grid>
            ) : null}
            <Grid
              container
              sx={{
                margin: isConfirmation ? '' : '16px 0',
                justifyContent: isConfirmation ? 'center' : 'flex-end',
                flexGrow: 6,
                ...sxProps?.dialogActionsSx,
              }}
            >
              {secondaryBtnText && (
                <Button
                  variant='outlined'
                  sx={{ marginRight: '16px' }}
                  onClick={secondaryBtnAction}
                  disabled={isSecondaryBtnDisabled}
                >
                  {secondaryBtnText}
                </Button>
              )}
              {primaryBtnText && (
                <Tooltip title={primaryBtnTooltip ?? ''} disableHoverListener={!primaryBtnTooltip}>
                  <span tabIndex={isPrimaryBtnDisabled && primaryBtnTooltip ? 0 : undefined}>
                    <Button
                      data-testid='loadingButtonIconId'
                      variant='contained'
                      sx={{ marginRight: isConfirmation ? '' : '16px' }}
                      onClick={primaryBtnAction}
                      disabled={isPrimaryBtnDisabled}
                      isLoading={isPrimaryBtnLoading}
                    >
                      {primaryBtnText}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Grid>
          </DialogActions>
        ) : null}
      </MuiDialog>
    </ThemeProvider>
  );
};
